import PageComponent from "../components/page/PageComponent.vue";
import SectionSheet from "../components/section_sheet/SectionSheet.vue";
import PageControl from "../components/page_control/PageControl.vue";
import { format_date } from "@/utils/date_formatter";

export default {
  name: "EnterpriseAmlVerificationReport",
  components: {
    PageControl,
    PageComponent,
    SectionSheet,
  },
  props: {
    sheet_elements: {
      type: String,
      default: ".sheet",
      required: false,
    },
    aml_data: {
      type: Object,
      default: () => ({}),
      required: false,
    },
  },
  data() {
    return {
      request_data: {},
      generated_date: new Date().toLocaleDateString("en-ZA", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
      nm_results: [],
      wls_results: [],
      export_options: {
        subject_info: false,
        negative_media: false,
        watchlist: false,
      },
      export_format: "csv",
      libraries_loaded: {
        papa_parse: false,
        sheetjs: false,
      },
    };
  },
  computed: {
    report_reference() {
      return "AML-" + (this.application_id || "N/A");
    },
    score_date() {
      const date_scored = this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes
        ?.DRWatchlistVerification?.TimeOfRequest;
      return date_scored
        ? new Date(date_scored).toLocaleDateString("en-ZA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : "";
    },
    result_matches() {
      var match_results = this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes?.DRWatchlistVerification?.MatchCount || 0;
      return match_results;
    },
    total_matches() {
      return (
        (this.nm_results?.length || 0) +
        (this.wls_results?.length || 0)
      );
    },
    // Limited results for display (max 11 per type)
    limited_nm_results() {
      return this.nm_results?.slice(0, 11) || [];
    },
    limited_wls_results() {
      return this.wls_results?.slice(0, 11) || [];
    },
    // Check if results exceed the limit
    nm_exceeds_limit() {
      return this.nm_results?.length > 11;
    },
    wls_exceeds_limit() {
      return this.wls_results?.length > 11;
    },    // Chunked results for pagination
    nm_result_chunks() {
      const chunks = [];
      const results = this.limited_nm_results;
      if (results.length === 0) return [];
      
      // First chunk gets 1 result (with header)
      chunks.push(results.slice(0, 1));
      
      // Subsequent chunks get 2 results each
      for (let i = 1; i < results.length; i += 2) {
        chunks.push(results.slice(i, i + 2));
      }
      return chunks;
    },
    wls_result_chunks() {
      const chunks = [];
      const results = this.limited_wls_results;
      if (results.length === 0) return [];
      
      // First chunk gets 1 result (with header)
      chunks.push(results.slice(0, 1));
      
      // Subsequent chunks get 2 results each
      for (let i = 1; i < results.length; i += 2) {
        chunks.push(results.slice(i, i + 2));
      }
      return chunks;
    },
    source_match() {
      if (this.request_data){
        return this.request_data.EnterpriseName;
      }

      // Combine both arrays into one (if they exist)
      const all_results = [
        ...(this.nm_results || []),
        ...(this.wls_results || [])
      ];
      
      // If there are no results, return "N/A"
      if (all_results.length === 0) {
        return "N/A";
      }
      
      // Sort the results by score in descending order (highest score first)
      all_results.sort((a, b) => (b.score || 0) - (a.score || 0));
      
      // Return the subjectMatched from the highest scoring result
      return all_results[0].subjectMatched || "N/A";
    },
    application_id() {
      return this.aml_data?.ResponseInfo?.ApplicationId || "";
    },
    nm_count() {
      return this.nm_results?.length || 0;
    },
    wls_count() {
      return this.wls_results?.length || 0;
    },
    watchlist_decision() {
      return (
        this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes
          ?.DRWatchlistVerification?.WatchlistDecision || "N/A"
      );
    },
    date_scored_formatted() {
      const date_scored =
        this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes
          ?.DRWatchlistVerification?.TimeOfRequest;
      if (!date_scored) return "N/A";
      try {
        return new Date(date_scored).toLocaleString("en-ZA", {
          dateStyle: "medium",
          timeStyle: "short",
        });
      } catch {
        return date_scored;
      }
    },
    verification_status_icon_class() {
      const decision = this.watchlist_decision.toLowerCase();
      if (decision === "approve") return "text-success";
      if (decision === "review") return "text-warning";
      return "text-danger";
    },
    has_selected_tables() {
      return Object.values(this.export_options).some((selected) => selected);
    },
  },
  mounted() {
    this.request_data = this.aml_data.RequestData || {};
    this.nm_results =
      this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes
        ?.DSWatchlistVerification?.NmResults || [];
    this.wls_results =
      this.aml_data?.Fields?.Applicants_IO?.Applicant[0]?.Attributes
        ?.DSWatchlistVerification?.WlsResults || [];
  },
  methods: {
    close_modal() {
      $("#export-modal-" + this.application_id).modal("hide");
    },
    show_export_modal() {
      this.export_options.subject_info = this.total_matches > 0;
      this.export_options.negative_media = this.nm_count > 0;
      this.export_options.watchlist = this.wls_count > 0;

      $("#export-modal-" + this.application_id).modal("show");
    },
    async load_script(src) {
      return new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) {
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },
    async ensure_libraries_loaded() {
      try {
        if (typeof window.Papa === "undefined") {
          await this.load_script(
            "https://cdnjs.cloudflare.com/ajax/libs/PapaParse/5.3.0/papaparse.min.js"
          );
        }
        if (typeof window.XLSX === "undefined") {
          await this.load_script(
            "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js"
          );
        }
      } catch (error) {
        console.error("Failed to load required libraries:", error);
        throw error;
      }
    },
    strip_html(html_string) {
      if (!html_string) return "";
      const temp_div = document.createElement("div");
      temp_div.innerHTML = html_string;
      return temp_div.textContent || temp_div.innerText || "";
    },
    get_current_date_string() {
      const current_date = new Date();
      return current_date.toISOString().split("T")[0];
    },
    prepare_subject_data() {
      return [
        {
          "Subject Name": this.source_match,
          "Application ID": this.application_id,
          "Verification Date": this.date_scored_formatted,
          "Risk Decision": this.watchlist_decision,
          "Negative Media Count": this.nm_count,
          "Watchlist Count": this.wls_count,
          "Total Matches": this.total_matches,
        }
      ];
    },
    prepare_negative_media_data() {
      return this.nm_results.map((result) => ({
        "Subject Matched": result.subjectMatched || "",
        Score: result.score || "",
        Category: result.category || "",
        "Source Country": result.sourceCountry || "",
        "Article Date": this.formatDate(result.articleDate) || "",
        "Article Title": result.articleTitle || "",
        Text: result.text || "",
        Highlights: this.strip_html(
          (result.highlights && result.highlights[0]) || ""
        ),
        URL: result.url || "",
        Concepts: (result.concepts && result.concepts.join(", ")) || "",
      }));
    },
    prepare_watchlist_data() {
      return this.wls_results.map((result) => ({
        "Subject Matched": result.subjectMatched || "",
        Score: result.score || "",
        Category: result.category || "",
        "Source Country": result.sourceCountry || "",
        "Source Agency": result.sourceAgencyName || "",
        "Source List Type": result.sourceListType || "",
        Text: result.text || "",
        Highlights: this.strip_html(
          (result.highlights && result.highlights[0]) || ""
        ),
        URL: result.url || "",
        Remarks: result.remarks || "",
      }));
    },
    async export_csv() {
      const Papa = window.Papa;
      const current_date = this.get_current_date_string();
      const tables_to_export = [];

      if (this.export_options.subject_info) {
        const subject_data = this.prepare_subject_data();
        const csv_data = Papa.unparse(subject_data);
        const filename = `AML_Subject_Info_${current_date}.csv`;
        tables_to_export.push({ data: csv_data, filename });
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        const nm_data = this.prepare_negative_media_data();
        const csv_data = Papa.unparse(nm_data);
        const filename = `AML_Negative_Media_${current_date}.csv`;
        tables_to_export.push({ data: csv_data, filename });
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        const wls_data = this.prepare_watchlist_data();
        const csv_data = Papa.unparse(wls_data);
        const filename = `AML_Watchlist_${current_date}.csv`;
        tables_to_export.push({ data: csv_data, filename });
      }

      tables_to_export.forEach((table) => {
        const blob = new Blob([table.data], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = table.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    async export_excel() {
      const XLSX = window.XLSX;
      const current_date = this.get_current_date_string();
      const workbook = XLSX.utils.book_new();

      if (this.export_options.subject_info) {
        const subject_data = this.prepare_subject_data();
        const worksheet = XLSX.utils.json_to_sheet(subject_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Subject Info");
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        const nm_data = this.prepare_negative_media_data();
        const worksheet = XLSX.utils.json_to_sheet(nm_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Negative Media");
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        const wls_data = this.prepare_watchlist_data();
        const worksheet = XLSX.utils.json_to_sheet(wls_data);
        XLSX.utils.book_append_sheet(workbook, worksheet, "Watchlist");
      }      const filename = `AML_Report_${current_date}.xlsx`;
      XLSX.writeFile(workbook, filename);
    },
    async export_json() {
      const current_date = this.get_current_date_string();
      const export_data = this.prepare_json_export_data();

      const json_content = JSON.stringify(export_data, null, 2);
      const blob = new Blob([json_content], {
        type: "application/json;charset=utf-8;",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", `AML_Report_${current_date}.json`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    prepare_json_export_data() {
      const export_data = {
        report_metadata: {
          report_reference: this.report_reference,
          generated_date: this.generated_date,
          export_timestamp: new Date().toISOString(),
          report_type: "Enterprise AML Verification Report"
        }
      };

      if (this.export_options.subject_info) {
        export_data.subject_information = this.prepare_subject_data()[0];
      }

      if (this.export_options.negative_media && this.nm_count > 0) {
        export_data.negative_media_results = this.prepare_negative_media_data();
      }

      if (this.export_options.watchlist && this.wls_count > 0) {
        export_data.watchlist_results = this.prepare_watchlist_data();
      }

      return export_data;
    },
    async export_data() {
      try {
        $("#export-modal-" + this.application_id).modal("hide");
        await this.ensure_libraries_loaded();

        if (this.export_format === "csv") {
          await this.export_csv();
        } else if (this.export_format === "excel") {
          await this.export_excel();
        } else if (this.export_format === "json") {
          await this.export_json();
        }
      } catch (error) {
        console.error("Export failed:", error);
        alert("Export failed. Please try again.");
      }
    },
    formatDate(date) {
      return format_date(date);
    },
    format_highlights(highlights) {
      if (
        !highlights ||
        !Array.isArray(highlights) ||
        highlights.length === 0
      ) {
        return "No highlights available";
      }
      return highlights[0]
        .replace(/<em>/g, "<strong>")
        .replace(/<\/em>/g, "</strong>");
    },
  },
};